import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchJourneyStepFieldMapping,
  saveJourneyStepFieldMappings,
  type AudienceMappingPreviewRow,
  type FieldMappingRow,
  type JourneyStepFieldMapping,
  type JourneyStepTemplateFieldMappings,
  type OurSourceField,
  type TemplateFieldMappingRule,
} from "./campaignBuilderApi";
import { CampaignContactLink, type CampaignCrmNavigation } from "./CrmRecordLinks";

function coverageClass(percent: number): string {
  if (percent >= 90) return "field-mapping-coverage-good";
  if (percent >= 50) return "field-mapping-coverage-mid";
  return "field-mapping-coverage-low";
}

function rulesFromPayload(
  mappings: FieldMappingRow[],
  saved: JourneyStepTemplateFieldMappings,
): Record<string, TemplateFieldMappingRule> {
  const out: Record<string, TemplateFieldMappingRule> = { ...saved.rules };
  for (const row of mappings) {
    if (out[row.target]) continue;
    out[row.target] = {
      mode: row.mode ?? "source",
      source_path: row.source_path ?? row.recommended_source,
      fixed_value: row.fixed_value ?? null,
    };
  }
  return out;
}

const PREVIEW_PRELIM_ROWS = 25;
const DEFAULT_SEND_FROM = "Ironfleet<noreply@ironfleet.ai>";

function MappingModeToggle({
  mode,
  disabled,
  onChange,
}: {
  mode: FieldMappingRow["mode"];
  disabled: boolean;
  onChange: (mode: "source" | "fixed") => void;
}) {
  return (
    <div className="field-mapping-mode-toggle" role="group" aria-label="Mapping type">
      <button
        type="button"
        className={mode === "source" ? "is-active" : ""}
        disabled={disabled}
        onClick={() => onChange("source")}
      >
        Audience
      </button>
      <button
        type="button"
        className={mode === "fixed" ? "is-active" : ""}
        disabled={disabled}
        onClick={() => onChange("fixed")}
      >
        Fixed
      </button>
    </div>
  );
}

export function FieldMappingModal({
  stepId,
  stepName,
  audienceFilter,
  campaignKey,
  busy,
  onClose,
  onSaved,
  crmNavigation,
}: {
  stepId: string;
  stepName: string;
  audienceFilter: Record<string, unknown> | null;
  campaignKey?: string | null;
  busy: boolean;
  onClose: () => void;
  onSaved?: (step: Record<string, unknown>) => void;
  crmNavigation?: CampaignCrmNavigation;
}) {
  const [expanded, setExpanded] = useState(false);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<JourneyStepFieldMapping | null>(null);
  const [rules, setRules] = useState<Record<string, TemplateFieldMappingRule>>({});
  const [audienceOverrides, setAudienceOverrides] = useState<Record<string, Record<string, string>>>({});
  const [gridRows, setGridRows] = useState<AudienceMappingPreviewRow[]>([]);
  const [audienceTargets, setAudienceTargets] = useState<string[]>([]);
  const [sendFrom, setSendFrom] = useState("");
  const [saving, setSaving] = useState(false);
  const [previewRefreshing, setPreviewRefreshing] = useState(false);
  const [showAllPreview, setShowAllPreview] = useState(false);
  const rulesReadyRef = useRef(false);
  const showAllPreviewRef = useRef(showAllPreview);
  showAllPreviewRef.current = showAllPreview;
  const draftRef = useRef({ rules, audienceOverrides });
  draftRef.current = { rules, audienceOverrides };

  const buildPayload = useCallback((): JourneyStepTemplateFieldMappings => {
    return {
      version: 1,
      rules,
      audience_overrides: audienceOverrides,
    };
  }, [audienceOverrides, rules]);

  const fetchMapping = useCallback(
    async (opts?: { silent?: boolean; withDraft?: boolean; applySavedDraft?: boolean }) => {
      if (!audienceFilter) {
        setMappingError("Load or save an audience filter first to measure coverage across recipients.");
        setMapping(null);
        setGridRows([]);
        setAudienceTargets([]);
        return;
      }
      const silent = opts?.silent ?? false;
      if (silent) setPreviewRefreshing(true);
      else setMappingLoading(true);
      setMappingError(null);
      try {
        const draft = opts?.withDraft ? draftRef.current : undefined;
        const audience_preview_limit = showAllPreviewRef.current ? 0 : PREVIEW_PRELIM_ROWS;
        const result = await fetchJourneyStepFieldMapping(stepId, audienceFilter, campaignKey ?? undefined, {
          audience_preview_limit,
          ...(draft
            ? { rules: draft.rules, audience_overrides: draft.audienceOverrides }
            : {}),
        });
        setMapping(result);
        setGridRows(result.audience_preview);
        setAudienceTargets(result.audience_sourced_targets ?? []);
        if (opts?.applySavedDraft) {
          setRules(rulesFromPayload(result.mappings, result.saved_mappings));
          setAudienceOverrides(result.saved_mappings.audience_overrides ?? {});
          setSendFrom(result.send_from ?? "");
          rulesReadyRef.current = true;
        }
      } catch (err: unknown) {
        setMappingError(err instanceof Error ? err.message : "Could not load field mapping");
        if (!opts?.withDraft) {
          setMapping(null);
          setGridRows([]);
          setAudienceTargets([]);
        }
      } finally {
        if (silent) setPreviewRefreshing(false);
        else setMappingLoading(false);
      }
    },
    [audienceFilter, campaignKey, stepId],
  );

  useEffect(() => {
    rulesReadyRef.current = false;
    setShowAllPreview(false);
    setRules({});
    setAudienceOverrides({});
    setSendFrom("");
    setMapping(null);
    setGridRows([]);
    setAudienceTargets([]);
    void fetchMapping({ applySavedDraft: true });
  }, [fetchMapping, stepId]);

  useEffect(() => {
    if (!rulesReadyRef.current) return;
    const timer = window.setTimeout(() => {
      void fetchMapping({ silent: true, withDraft: true });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [fetchMapping, rules]);

  const sourceFields = mapping?.source_fields ?? [];

  function updateRule(target: string, patch: Partial<TemplateFieldMappingRule>) {
    setRules((prev) => {
      const current = prev[target] ?? { mode: "source", source_path: null };
      const next = { ...current, ...patch };
      if (patch.mode === "fixed" && next.fixed_value == null) {
        next.fixed_value = "";
      }
      return { ...prev, [target]: next };
    });
  }

  function setAudienceCell(contactId: string, target: string, value: string) {
    setAudienceOverrides((prev) => {
      const next = { ...prev };
      const row = { ...(next[contactId] ?? {}) };
      const trimmed = value.trim();
      if (trimmed) row[target] = value;
      else delete row[target];
      if (Object.keys(row).length) next[contactId] = row;
      else delete next[contactId];
      return next;
    });
    setGridRows((rows) =>
      rows.map((row) =>
        row.contact_id === contactId ? { ...row, cells: { ...row.cells, [target]: value } } : row,
      ),
    );
  }

  async function onSaveNow() {
    setSaving(true);
    try {
      const { step } = await saveJourneyStepFieldMappings(
        stepId,
        buildPayload(),
        sendFrom.trim() ? sendFrom.trim() : undefined,
      );
      onSaved?.(step);
      await fetchMapping({ withDraft: true });
      setMappingError(null);
    } catch (err: unknown) {
      setMappingError(err instanceof Error ? err.message : "Save field mappings failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-backdrop field-mapping-backdrop" role="dialog" aria-modal="true" aria-labelledby="field-mapping-title">
      <div className={`modal-card field-mapping-modal${expanded ? " is-expanded" : ""}`}>
        <div className="field-mapping-modal-head">
          <div>
            <h3 id="field-mapping-title">Field mapping — {stepName}</h3>
            <p className="hint field-mapping-modal-sub">
              Coverage and preview use only contacts planned for this email. Fixed fields use one literal value for every
              recipient; audience preview shows audience-sourced fields only.
            </p>
          </div>
          <div className="field-mapping-head-actions">
            <button type="button" className="secondary field-mapping-expand" onClick={() => setExpanded((v) => !v)}>
              {expanded ? "Restore size" : "Expand"}
            </button>
          </div>
        </div>

        {mappingError ? (
          <p className="hint test-send-mapping-error" role="alert">
            {mappingError}
          </p>
        ) : null}

        <label className="campaign-builder-field field-mapping-send-from">
          <span>Send from</span>
          <input
            type="text"
            value={sendFrom}
            onChange={(e) => setSendFrom(e.target.value)}
            placeholder={DEFAULT_SEND_FROM}
            disabled={busy || saving || mappingLoading}
          />
          <span className="hint">Use <code>Name&lt;email&gt;</code> or plain email.</span>
        </label>

        {mappingLoading && !mapping ? <p className="hint">Loading mappings from campaign email…</p> : null}

        {mapping ? (
          <div className="field-mapping-modal-body">
            <section className="field-mapping-rules-section">
              <h4>Field rules</h4>
              <p className="hint">
                {mapping.mappings.length} mapped field{mapping.mappings.length === 1 ? "" : "s"} ·{" "}
                <strong>{mapping.universe_total}</strong> planned recipient
                {mapping.universe_total === 1 ? "" : "s"}
              </p>
              {mapping.universe_total === 0 ? (
                <p className="hint test-send-mapping-error" role="status">
                  No contacts are planned for this email. Assign recipients in the audience builder first.
                </p>
              ) : null}
              {mapping.mappings.length ? (
                <div className="field-mapping-rules-panel">
                  <table className="rating-table field-mapping-rules-table">
                    <thead>
                      <tr>
                        <th>SendGrid field</th>
                        <th>Mapping type</th>
                        <th>Configuration</th>
                        <th>Coverage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mapping.mappings.map((row) => {
                        const rule = rules[row.target] ?? {
                          mode: row.mode ?? "source",
                          source_path: row.source_path ?? row.recommended_source,
                          fixed_value: row.fixed_value ?? "",
                        };
                        const mode = rule.mode ?? "source";
                        return (
                          <tr key={row.target}>
                            <td><code>{row.target}</code></td>
                            <td>
                              <MappingModeToggle
                                mode={mode}
                                disabled={busy || saving}
                                onChange={(nextMode) => updateRule(row.target, { mode: nextMode })}
                              />
                            </td>
                            <td className="field-mapping-config-cell">
                              {mode === "source" ? (
                                <select
                                  value={rule.source_path ?? ""}
                                  disabled={busy || saving}
                                  onChange={(e) => updateRule(row.target, { source_path: e.target.value || null })}
                                >
                                  <option value="">Auto ({row.recommended_source ?? "none"})</option>
                                  {(sourceFields as OurSourceField[]).map((field) => (
                                    <option key={field.path} value={field.path}>
                                      {field.label} ({field.path})
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  className="field-mapping-fixed-value"
                                  value={rule.fixed_value ?? ""}
                                  placeholder="Fixed value for all recipients"
                                  disabled={busy || saving}
                                  onChange={(e) => updateRule(row.target, { fixed_value: e.target.value })}
                                />
                              )}
                            </td>
                            <td>
                              {mode === "source" ? (
                                <span className={`field-mapping-coverage ${coverageClass(row.coverage.percent)}`}>
                                  {row.coverage.populated}/{row.coverage.total} ({row.coverage.percent}%)
                                </span>
                              ) : (
                                <span className="hint">Fixed</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="hint">No dynamic fields detected on this template.</p>
              )}
            </section>

            <section className="field-mapping-audience-section">
              <div className="field-mapping-audience-head">
                <div>
                  <h4>Audience data preview</h4>
                  <p className="hint">
                    Audience-sourced fields only · showing {gridRows.length} of {mapping.universe_total} contacts
                    {previewRefreshing ? " · updating…" : ""}
                    {!showAllPreview && mapping.universe_total > PREVIEW_PRELIM_ROWS
                      ? ` (first ${PREVIEW_PRELIM_ROWS})`
                      : ""}
                    {showAllPreview && mapping.audience_preview_truncated ? " (max 10,000)" : ""}
                  </p>
                  <label className="field-mapping-show-all hint">
                    <input
                      type="checkbox"
                      checked={showAllPreview}
                      disabled={busy || saving || mappingLoading || previewRefreshing}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        showAllPreviewRef.current = checked;
                        setShowAllPreview(checked);
                        void fetchMapping({ silent: true, withDraft: true });
                      }}
                    />
                    Show all qualified contacts ({mapping.universe_total})
                  </label>
                </div>
                <button
                  type="button"
                  className="secondary field-mapping-refresh"
                  disabled={busy || saving || mappingLoading || previewRefreshing}
                  onClick={() => void fetchMapping({ silent: true, withDraft: true })}
                >
                  {previewRefreshing ? "Refreshing…" : "Refresh preview"}
                </button>
              </div>
              {audienceTargets.length && gridRows.length ? (
                <div className="field-mapping-audience-scroll">
                  <table className="rating-table field-mapping-audience-grid">
                    <thead>
                      <tr>
                        <th className="field-mapping-sticky-col">Contact</th>
                        <th className="field-mapping-sticky-col">Email</th>
                        {audienceTargets.map((target) => (
                          <th key={target}><code>{target}</code></th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {gridRows.map((row) => (
                        <tr key={row.contact_id}>
                          <td className="field-mapping-sticky-col">
                            <CampaignContactLink
                              contactId={row.contact_id}
                              label={row.name || "—"}
                              navigation={crmNavigation}
                            />
                          </td>
                          <td className="field-mapping-sticky-col hint">{row.email}</td>
                          {audienceTargets.map((target) => (
                            <td key={target}>
                              <input
                                type="text"
                                className="field-mapping-grid-cell"
                                value={row.cells[target] ?? ""}
                                disabled={busy || saving}
                                onChange={(e) => setAudienceCell(row.contact_id, target, e.target.value)}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="hint field-mapping-audience-empty">
                  {audienceTargets.length
                    ? "No audience rows to preview."
                    : "Switch at least one field to Audience mapping to preview per-contact values."}
                </p>
              )}
            </section>
          </div>
        ) : null}

        <div className="campaign-builder-actions field-mapping-modal-actions">
          <button type="button" disabled={busy || saving || mappingLoading} onClick={() => void onSaveNow()}>
            {saving ? "Saving…" : "Save mappings"}
          </button>
          <button type="button" disabled={busy || saving} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
